import { createFormHeaderImageAttachment } from '@/lib/api/formApi'
import { getColorBrightness, isLightColor } from '@/lib/utils/colors'
import { AIGeneratedForm } from '@/redux/slices/formAIGenerationSlice'
import { CreateFormInput, FieldAttributes, Layout, Scene, ThemeAttributes, ThemeHeaderType, ThemeUiVersion } from '@/typings/domain'
import buildField, { AI_SUPPORTED_FIELD_TYPES } from './buildField/buildField'

const LIGHT_COLOR_BRIGHTNESS_THRESHOLD = 186

function buildThemeButtonColorStyle(theme: NonNullable<AIGeneratedForm['theme']>) {
  const primaryColor = theme?.headerImageAvgColor
  if (!primaryColor) return

  if (getColorBrightness(primaryColor) > LIGHT_COLOR_BRIGHTNESS_THRESHOLD) {
    return // use default primary color
  }

  return {
    backgroundColor: primaryColor,
    color: isLightColor(primaryColor) ? '#000' : '#fff',
  }
}

export function buildFormAttributes(form: AIGeneratedForm, backendMessageId: string | undefined): CreateFormInput {
  return {
    scene: form.scene,
    layout: Layout.Classic,
    createdFrom: 'ai',
    createSource: 'nextApp',
    aiMessageId: backendMessageId,
    attributes: {
      name: form.name ?? '',
      description: form.description,
      fields: form.fields
        ?.filter(field => AI_SUPPORTED_FIELD_TYPES.includes(field.type))
        .map((field, index) => ({
          ...buildField({ rawField: field, scene: form.scene ?? Scene.Form }),
          position: index,
        })) as FieldAttributes[],
    },
  }
}

const formHeaderAttrs = {
  fontFamilyRaw: 'inherit',
  fontSize: '24px',
  fontWeight: 'bold',
  color: '#000000',
  textAlign: 'left',
}

const textStyleAttrs = {
  fontFamilyRaw: 'inherit',
  fontSize: '16px',
  fontWeight: 'normal',
  color: '#222222',
}

const fieldLabelAttrs = {
  ...textStyleAttrs,
  fontWeight: 'bold',
  color: '#000000',
}

const buttonStyleAttrs = {
  fontFamilyRaw: 'inherit',
  fontSize: '14px',
  fontWeight: 'normal',
  color: '#ffffff',
  backgroundColor: '#2563eb',
  borderColor: '#ffffff',
  borderWidth: '0',
}

const submitButtonAttrs = {
  ...buttonStyleAttrs,
  padding: '0 55px',
  fontSize: '16px',
  fontWeight: 'bold',
}

/** Record<url, attachmentId> */
let createdHeaderImageAttachmentsCache: Record<string, string> = {}

export function clearCreatedHeaderImageAttachmentsCache() {
  createdHeaderImageAttachmentsCache = {}
}

export async function buildFormThemeAttributes(
  theme: NonNullable<AIGeneratedForm['theme']>,
  headerImageReplacements?: Record<string, string>
): Promise<ThemeAttributes> {
  let headerImageAttachmentId: string | undefined

  if (theme?.headerImageUrl) {
    const url = headerImageReplacements?.[theme.headerImageUrl] || theme.headerImageUrl
    headerImageAttachmentId = createdHeaderImageAttachmentsCache[url]
    if (!headerImageAttachmentId) {
      const res = await createFormHeaderImageAttachment({ imageUrl: url })
      headerImageAttachmentId = res.data?.createFormHeaderImageAttachment?.attachment?.id
      if (headerImageAttachmentId) createdHeaderImageAttachmentsCache[url] = headerImageAttachmentId
    }
  }

  const buttonColorStyle = buildThemeButtonColorStyle(theme)

  return {
    uiVersion: ThemeUiVersion.Beautiform,
    wallpaper: { backgroundColor: '#f3f4f6' },
    typography: {
      formHeader: formHeaderAttrs,
      fieldLabel: fieldLabelAttrs,
      choiceStyle: textStyleAttrs,
    },
    header: headerImageAttachmentId
      ? { type: ThemeHeaderType.Image, imageAttachmentId: headerImageAttachmentId }
      : { type: ThemeHeaderType.None },
    generalButton: { style: { ...buttonStyleAttrs, ...buttonColorStyle } },
    submitButton: { style: { ...submitButtonAttrs, ...buttonColorStyle } },
  }
}
